import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../styles/BookingDetails.css';
import { AuthContext } from '../context/AuthContext';
import DesktopLayout from '../layouts/DesktopLayout';
import BookingChannexInfo from '../components/BookingChannexInfo';
import BookingGuestRequestsSection from '../components/BookingGuestRequestsSection';
import OpenComplaintButton from '../components/complaints/OpenComplaintButton';
import BookingCheckinMessagesSection from '../components/BookingCheckinMessagesSection';
import EditBookingModal from '../components/EditBookingModal';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import { formatCount } from '../utils/numberFormatter';
import { toRiyadhInputValue, riyadhInputValueToIso, riyadhDayIso } from '../utils/dateValueHelper';
import guestArrivalApi from '../services/guestArrivalApi';
import ARRIVAL_ERROR_CODES from '../utils/arrivalErrorCodes';
import relocationApi from '../services/relocationApi';
import RelocationBanner from '../components/RelocationBanner';
import RelocateGuestModal from '../components/RelocateGuestModal';
import RelocationStepsPanel from '../components/RelocationStepsPanel';
import { RELOCATION_ACTION, RELOCATION_UNKNOWN_AR } from '../config/relocationLabels';
import { CORE_STAFF_ROLES, BILLING_MANAGE_ROLES } from '../config/roleConfig';
import commissionTierApi from '../services/commissionTierApi';
import departureApi from '../services/departureApi';
import turnoverApi from '../services/turnoverApi';
import TurnoverWindowNote from '../components/TurnoverWindowNote';
import { TURNOVER_ERROR_CODES, TURNOVER_VERDICT } from '../utils/turnoverCodes';
import { formatHoursMinutesAr } from '../utils/numberFormatter';
import DEPARTURE_ERROR_CODES from '../utils/departureErrorCodes';
import { vatPercentSuffix } from '../utils/vatPricing';
// Shared with the Inc 7 inbox context panel — see the note above BookingDetails below.
import {
  ARRIVAL_VIEW,
  ARRIVAL_REASON_AR,
  ARRIVAL_REASON_UNKNOWN,
  ARRIVAL_NO_EVIDENCE_HINT,
  ARRIVAL_ERROR_AR
} from '../config/arrivalLabels';

// L48 — the access-code card shows TWO orthogonal badges that answer different
// questions and can both be true at once:
//   (a) حالة الحجز — stay status, derived from the validity WINDOW (time).
//   (b) حالة القفل — lock-delivery status, from the TTLock lifecycle (delivery).
// The dangerous combo (نشط + فشل المزامنة = guest at the door, code never landed)
// is highlighted prominently.

// Badge (a): stay status from the window — accurate even if stored accessStatus
// is stale (the worker only stamps it at create/update). "Resolve on read" (2a).
// The access window can be null (a FAILED create stamps accessStatus='failed'
// but leaves accessValidFrom/Until unset), so fall back to the booking's real
// check-in/out dates — never default to "active", or a future failed-create
// booking would falsely read نشط and fire the danger banner.
const deriveStayState = (booking) => {
  if (booking.status === 'cancelled') return { key: 'cancelled', label: 'ملغي', cls: 'access-badge-cancelled' };
  const now = Date.now();
  const from = booking.accessValidFrom ? new Date(booking.accessValidFrom).getTime()
    : (booking.checkIn ? new Date(booking.checkIn).getTime() : null);
  const until = booking.accessValidUntil ? new Date(booking.accessValidUntil).getTime()
    : (booking.checkOut ? new Date(booking.checkOut).getTime() : null);
  if (until && now > until) return { key: 'ended', label: 'منتهي', cls: 'access-badge-expired' };
  if (from && now < from) return { key: 'pending', label: 'قيد الانتظار', cls: 'access-badge-pending' };
  if (from && now >= from) return { key: 'active', label: 'نشط', cls: 'access-badge-active' };
  return { key: 'unknown', label: 'غير محدد', cls: 'access-badge-none' };
};

// Badge (b): lock-delivery status from LockAutomationState.actual.lifecycleStatus
// (projected as accessLifecycle by GET /:id). PENDING + CREATED_IN_CLOUD merge
// into one ops label "قيد المزامنة" (L48). DELETED/none → no badge.
const deriveLockState = (lifecycle) => {
  switch (lifecycle) {
    case 'CONFIRMED_ON_LOCK': return { label: 'مؤكد على القفل', cls: 'access-badge-confirmed' };
    case 'PENDING':
    case 'CREATED_IN_CLOUD':
    // REPAIRING (R2) is a transient the repair workflow owns — the code is being
    // replaced on the lock. It reads as the same benign "قيد المزامنة" as the other
    // in-progress states (never the فشل danger badge): a system fixing itself is not
    // broken. Only a genuine unrepaired FAILED fires the danger banner below.
    case 'REPAIRING': return { label: 'قيد المزامنة', cls: 'access-badge-syncing' };
    case 'FAILED': return { label: 'فشل المزامنة', cls: 'access-badge-failed' };
    default: return null;
  }
};

// حالة تسجيل دخول الضيف — arrival EVIDENCE, resolved by the backend (be/services/guestArrival).
// The frontend renders the verdict; it never re-derives one.
//
// The Arabic vocabulary moved to `config/arrivalLabels.js` when the Inc 7 inbox
// panel became the second consumer. One resolver gives the two surfaces the same
// ANSWER; one label module gives them the same WORDS — and of the two kinds of
// drift, the wording is the one that would never show up in a diff.
/**
 * Arabic label for a booking's VAT footing, or null when we do not know.
 *
 * Deliberately NOT a lookup by platform: `services/vatFooting.js` stamps the answer on the
 * booking at ingestion from the platform's own tax fields, and that stamp is the only thing
 * entitled to answer. Returning null for 'unknown' and for rows predating the field keeps the
 * page silent rather than confident — the same reason billing refuses to bill an unknown footing.
 */
const vatFootingLabel = (vatFooting) => {
  if (vatFooting?.value === 'exclusive') return 'غير شامل الضريبة';
  if (vatFooting?.value === 'inclusive') return 'شامل الضريبة';
  return null;
};

const BookingDetails = () => {
  const { bookingId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Operational times (Phase 2a) edit state
  const [editingOps, setEditingOps] = useState(false);
  const [opsArrival, setOpsArrival] = useState('');
  const [opsDeparture, setOpsDeparture] = useState('');
  // Baseline the pickers were seeded with (date prefilled from check-in/out + policy time
  // when unset). Used to preserve "unset" semantics: an unchanged seed saves as null.
  const [opsArrivalSeed, setOpsArrivalSeed] = useState('');
  const [opsDepartureSeed, setOpsDepartureSeed] = useState('');
  const [savingOps, setSavingOps] = useState(false);
  const [opsError, setOpsError] = useState(null);
  // Cleaning window either side of the times being edited (tasks/arrival-readiness-action-side.md
  // Phase 2). Server-computed on every change — the same function the save enforces with.
  const [opsTurnover, setOpsTurnover] = useState(null);
  const [opsTurnoverLoading, setOpsTurnoverLoading] = useState(false);
  const [opsNotice, setOpsNotice] = useState(null);

  // Arrival evidence (حالة تسجيل دخول الضيف). Its own fetch, not a field on the
  // booking: one resolver, one endpoint, reused verbatim by the inbox panel later.
  const [arrival, setArrival] = useState(null);
  // A failed lookup must be VISIBLE. The whole feature exists so absence of data
  // is never read as absence of a guest — a card that silently vanishes on error
  // commits that same error one level up, and ops would never know the surface
  // was broken.
  const [arrivalError, setArrivalError] = useState(false);
  // Where the guest actually IS. Null is a real answer — "in the unit they booked" — which is every
  // booking today, so the banner simply does not render.
  const [relocation, setRelocation] = useState(null);
  // ⚠️ Whether we KNOW that. Same reason as `arrivalError` above, and load-bearing twice over: a
  // silent failure would let "no banner" read as "the guest is in the unit on the booking", and it
  // would re-offer «نقل الضيف» on a guest who has already been moved.
  const [relocationError, setRelocationError] = useState(false);
  const [relocationKnown, setRelocationKnown] = useState(false);
  const [showRelocate, setShowRelocate] = useState(false);

  // Commission tier — who supplied the demand (owner-sourced-commission-tier.md).
  const [tierSaving, setTierSaving] = useState(false);
  const [tierError, setTierError] = useState(null);
  const [tierReason, setTierReason] = useState('');
  const [tierEditing, setTierEditing] = useState(false);
  // Bumped ONLY when a move is created, to make the steps panel appear. Deliberately not bumped by
  // the panel's own refresh — the panel calls back so the banner re-reads, and a bump there would
  // reload the panel, which would call back again.
  const [relocationVersion, setRelocationVersion] = useState(0);

  // Checkout record (tasks/manual-guest-checkout-event.md) — «هل غادر الضيف فعلاً؟».
  //
  // Unlike arrival, this is a STORED field, so it arrives on the booking payload rather than from
  // its own resolver: arrival is DERIVED from lock events and earns an endpoint; this is data.
  // Two-step entry state — the hour is typed, never assumed from the press (D14).
  const [checkoutEntryOpen, setCheckoutEntryOpen] = useState(false);
  const [checkoutValue, setCheckoutValue] = useState('');
  const [savingCheckout, setSavingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Ops check-in confirmation (tasks/manual-guest-arrival-confirm.md) — the «دخول» row's human half,
  // for a guest let in on an internal/emergency code whom the lock never saw. Stored on the booking
  // like the checkout record; same two-step entry (the hour is typed, never assumed from the press).
  const [arrivalEntryOpen, setArrivalEntryOpen] = useState(false);
  const [arrivalValue, setArrivalValue] = useState('');
  const [savingArrival, setSavingArrival] = useState(false);
  const [arrivalConfirmError, setArrivalConfirmError] = useState(null);

  // Navigation state
  const [activeNavItem, setActiveNavItem] = useState('bookings');

  // TurnKey owners see a simplified booking view — Channex/processing/change-log details are hidden
  const isTurnKeyOwner = user?.role === 'turnKeyOwner';

  // Operational settings are an internal ops tool — admin/ops/ops-manager only, never owners.
  // From roleConfig, not retyped: the backend gates these same surfaces on its own CORE_STAFF_ROLES,
  // and two hand-maintained copies of one list is how the two enforcement points drift apart.
  const isCoreStaff = CORE_STAFF_ROLES.includes(user?.role);
  const canSetTier = BILLING_MANAGE_ROLES.includes(user?.role);

  // Only a manually-keyed booking can carry a tier. An OTA guest is Host-Me-sourced by
  // definition, and the server refuses the change anyway — this keeps the control from
  // appearing where pressing it could only fail.
  const isManualBooking =
    booking?.sourceType === 'manual' ||
    booking?.sourcePlatform === 'direct' ||
    booking?.sourcePlatform === 'manual';

  /**
   * Move the commission tier. Refetches rather than patching local state, so the change
   * log on this page shows the entry the server actually wrote.
   */
  const handleTierChange = async (nextTier) => {
    setTierSaving(true);
    setTierError(null);
    try {
      await commissionTierApi.set(booking._id, nextTier, tierReason);
      setTierEditing(false);
      setTierReason('');
      await fetchBookingDetails();
    } catch (err) {
      // The server's message names the blocking month on a 409 — surface it verbatim
      // rather than a generic failure the operator cannot act on.
      setTierError(err.response?.data?.error || 'تعذّر تحديث مصدر الحجز');
    } finally {
      setTierSaving(false);
    }
  };

  useEffect(() => {
    fetchBookingDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  // Moving to ANOTHER booking in the same page instance (the cleaning-window shortcut links from
  // one booking's time editor to its neighbour's page) must not carry per-booking edit state with
  // it — an open time editor would otherwise show one guest's times on another guest's booking,
  // one «حفظ» away from saving them there.
  useEffect(() => {
    setEditingOps(false);
    setOpsError(null);
    setOpsTurnover(null);
    setOpsNotice(null);
    setCheckoutEntryOpen(false);
    setCheckoutError(null);
  }, [bookingId]);

  // Ops-only, and deliberately independent of the booking fetch: arrival evidence
  // is supporting context, so a failure here must never blank the booking page.
  useEffect(() => {
    if (!isCoreStaff || !bookingId) return;
    let cancelled = false;
    setArrival(null);
    setArrivalError(false);
    guestArrivalApi.get(bookingId)
      .then((data) => {
        if (cancelled) return;
        if (data?.success && data.arrival) setArrival(data.arrival);
        else setArrivalError(true);   // 200 without a verdict is still a failure to answer
      })
      .catch((err) => {
        console.error('Error fetching guest arrival:', err);
        if (!cancelled) setArrivalError(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, isCoreStaff]);

  // Ops-only, and deliberately independent of the booking fetch for the same reason as the arrival
  // evidence above: this is supporting context, and a failure here must never blank the page.
  //
  // ⚠️ A failure is NOT rendered as "no relocation". Silence would let a fetch error read as "the
  // guest is in the unit on the booking" — the one wrong answer here — so a failure says so out
  // loud AND withdraws the move button, because offering a move we cannot see the current state of
  // is how a guest ends up recorded in two units.
  useEffect(() => {
    if (!isCoreStaff || !bookingId) return;
    let cancelled = false;
    setRelocation(null);
    setRelocationError(false);
    setRelocationKnown(false);
    relocationApi.getCurrent(bookingId)
      .then((data) => {
        if (cancelled) return;
        if (data?.success) { setRelocation(data.relocation); setRelocationKnown(true); }
        else setRelocationError(true);   // 200 without an answer is still a failure to answer
      })
      .catch((err) => {
        console.error('Error fetching relocation:', err);
        if (!cancelled) setRelocationError(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, isCoreStaff]);

  // Called after a move is recorded. A failure here is the dangerous one — the move DID happen and
  // the page would still be showing the unit it was made from — so it fails loudly too.
  const refreshRelocation = () => {
    if (!isCoreStaff || !bookingId) return;
    relocationApi.getCurrent(bookingId)
      .then((data) => {
        if (data?.success) { setRelocation(data.relocation); setRelocationKnown(true); setRelocationError(false); }
        else setRelocationError(true);
      })
      .catch((err) => {
        console.error('Error refreshing relocation:', err);
        setRelocationError(true);
      });
  };

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/bookings/${bookingId}`);

      if (response.data.success) {
        setBooking(response.data.booking);
      } else {
        setError(response.data.message || 'فشل في جلب بيانات الحجز');
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError(err.response?.data?.message || 'حدث خطأ في جلب بيانات الحجز');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟')) {
      return;
    }

    try {
      setCancelling(true);
      const response = await axios.patch(
        `/api/v1/bookings/${bookingId}/cancel`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        alert('تم إلغاء الحجز بنجاح');
        // Refresh booking details to show updated status
        await fetchBookingDetails();
      } else {
        alert(response.data.message || 'فشل في إلغاء الحجز');
      }
    } catch (err) {
      console.error('Error cancelling booking:', err);
      alert(err.response?.data?.message || 'حدث خطأ في إلغاء الحجز');
    } finally {
      setCancelling(false);
    }
  };


  // Seed a datetime-local value: when an operational time is set, use it; when unset, prefill
  // the DATE from the booking's check-in/out and the TIME from the property's check-in/out
  // policy (canonical bookingPageSettings, default 15:00/11:00) so ops only adjusts the time
  // instead of re-entering the date. datetime-local is all-or-nothing, so a time is required.
  //
  // Everything here is Riyadh, matching the label under the card and the backend's
  // safeComposeRiyadh. It used to be browser-local, so a time entered outside Saudi
  // was stored — and used for the door code — hours off what was typed.
  const seedOpsValue = (storedValue, bookingDate, policyTime) => {
    if (storedValue) return toRiyadhInputValue(storedValue);
    const day = riyadhDayIso(bookingDate);
    return day ? `${day}T${policyTime}` : '';
  };

  // The arrival time ops have COMMITTED to. A Channex `arrival_hour` is the guest's request, not a
  // grant (Phase 2 L11): it is shown beside the picker as «الضيف طلب الوصول … — غير معتمد», and the
  // picker itself opens on the unit's check-in — the time the cleaning window is actually graded
  // against. Seeding it with the request made the box say 10:00 while the window was 16:00-based.
  const committedArrivalAt = () =>
    (booking.expectedArrivalSource === 'channex' ? null : booking.expectedArrivalAt);

  const startEditingOps = () => {
    const checkInTime = booking.property?.bookingPageSettings?.checkInTime || '15:00';
    const checkOutTime = booking.property?.bookingPageSettings?.checkOutTime || '11:00';
    const arrivalSeed = seedOpsValue(committedArrivalAt(), booking.checkIn, checkInTime);
    const departureSeed = seedOpsValue(booking.expectedDepartureAt, booking.checkOut, checkOutTime);
    setOpsArrival(arrivalSeed);
    setOpsDeparture(departureSeed);
    setOpsArrivalSeed(arrivalSeed);
    setOpsDepartureSeed(departureSeed);
    setOpsError(null);
    setOpsNotice(null);
    setOpsTurnover(null);
    setEditingOps(true);
  };

  // What the form would change, field by field: undefined = untouched (omitted), null = back to
  // the unit's normal time, else an ISO instant. Only fields ops actually CHANGED are sent:
  //  - the turnover guard blocks only on a side the edit touches, so resending an unchanged time
  //    would let a window that was already tight refuse an edit to the other field;
  //  - a box left as it opened must never overwrite or clear what is stored — in particular a
  //    guest's Channex request, which the arrival box deliberately does not show.
  // `committed` is the stored value the box was seeded from (the arrival's Channex request is not
  // committed, so granting exactly the requested hour still saves — as an ops grant).
  const buildOpsChange = () => {
    const fieldChange = (current, seed, committed) => {
      if (current === seed) return undefined;             // untouched
      if (!current) return committed ? null : undefined;  // cleared → back to the unit's time
      const iso = riyadhInputValueToIso(current);
      if (!iso) return undefined;
      const committedIso = committed ? new Date(committed).toISOString() : null;
      return iso === committedIso ? undefined : iso;
    };
    return {
      expectedArrivalAt: fieldChange(opsArrival, opsArrivalSeed, committedArrivalAt()),
      expectedDepartureAt: fieldChange(opsDeparture, opsDepartureSeed, booking.expectedDepartureAt)
    };
  };

  const handleSaveOperationalTimes = async () => {
    const change = buildOpsChange();
    if (change.expectedArrivalAt === undefined && change.expectedDepartureAt === undefined) {
      setEditingOps(false);
      return;
    }
    try {
      setSavingOps(true);
      setOpsError(null);
      const data = await turnoverApi.saveOperationalTimes(bookingId, change);

      if (data.success) {
        setBooking((prev) => ({
          ...prev,
          expectedArrivalAt: data.booking.expectedArrivalAt,
          expectedDepartureAt: data.booking.expectedDepartureAt,
          expectedArrivalSource: data.booking.expectedArrivalSource
        }));
        // L2: a tight window saves, and says so once.
        const tight = [data.turnover?.arrival, data.turnover?.departure]
          .filter((side) => side?.status === 'tight')
          .map((side) => side.minutes);
        setOpsNotice(data.turnover?.verdict === TURNOVER_VERDICT.WARN && tight.length
          ? `تم الحفظ — نافذة التنظيف ضيقة (${formatHoursMinutesAr(Math.min(...tight))})`
          : null);
        setEditingOps(false);
      } else {
        setOpsError(data.message || 'فشل تحديث الإعدادات التشغيلية');
      }
    } catch (err) {
      console.error('Error saving operational times:', err);
      const body = err.response?.data;
      if (body?.code === TURNOVER_ERROR_CODES.WINDOW_NEGATIVE && body.details?.turnover) {
        // Someone changed the neighbour since the live line was drawn. Branch on the CODE and let
        // the live line — the approved copy — say why; the server's Arabic `error` is for logs.
        setOpsTurnover(body.details.turnover);
      } else {
        setOpsError(body?.message || body?.error || 'حدث خطأ في تحديث الإعدادات التشغيلية');
      }
    } finally {
      setSavingOps(false);
    }
  };

  // Live cleaning-window line (L5): re-asked of the server as ops picks a time, debounced so
  // typing a datetime does not fire a request per keystroke.
  useEffect(() => {
    if (!editingOps || !booking) return undefined;
    let cancelled = false;
    setOpsTurnoverLoading(true);
    const timer = setTimeout(() => {
      turnoverApi.preview(bookingId, buildOpsChange())
        .then((data) => { if (!cancelled && data?.success) setOpsTurnover(data.turnover); })
        .catch((err) => {
          console.error('Error previewing cleaning window:', err);
          if (!cancelled) setOpsTurnover(null);
        })
        .finally(() => { if (!cancelled) setOpsTurnoverLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingOps, opsArrival, opsDeparture, bookingId]);

  // Landing from the cleaning-window shortcut «الضيف السابق غادر؟ سجّل وقت مغادرته» (Phase 2 L9) on
  // the NEXT guest's booking: bring the checkout record into view with its entry open. The param is
  // dropped once used, so a refresh does not reopen it.
  useEffect(() => {
    if (!booking || !isCoreStaff || searchParams.get('focus') !== 'checkout') return;
    // Wait for the TARGET booking: until its fetch lands, `booking` is still the page we came from.
    if (String(booking._id) !== String(bookingId)) return;
    document.getElementById('booking-checkout')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (!booking.effectiveCheckout) openCheckoutEntry();
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?._id, bookingId, isCoreStaff, searchParams]);

  /* ── Checkout record (tasks/manual-guest-checkout-event.md) ──────────────────────────────────
   *
   * 🔴 RECORD ONLY (D2). This notifies nobody and dispatches no cleaner. It also never touches
   *    `expectedDepartureAt` (D9) — that is the permission the guest was granted and it drives the
   *    door code's expiry; this is a fact somebody observed after the event.
   * 🔴 NAMING (D16). «أكد الفريق خروج الضيف», never «الخروج الفعلي» — a person confirmed it.
   */
  const openCheckoutEntry = () => {
    // Seeded fresh on each open, never at mount: a page left sitting would prefill a stale "now".
    setCheckoutValue(toRiyadhInputValue(new Date()));
    setCheckoutError(null);
    setCheckoutEntryOpen(true);
  };

  const handleRecordCheckout = async () => {
    // datetime-local hands back a naive wall-clock string the browser reads in ITS OWN zone —
    // composing it against Riyadh is what stops an operator abroad recording an hour nobody typed.
    const observedAt = riyadhInputValueToIso(checkoutValue);
    if (!observedAt) {
      setCheckoutError('وقت الخروج غير صالح');
      return;
    }
    try {
      setSavingCheckout(true);
      setCheckoutError(null);
      const data = await departureApi.recordGuestCheckout(bookingId, observedAt);
      if (data?.success) {
        // Mirror the server's winning record rather than the value just typed: a second operator
        // may have recorded it first, and the response carries whichever one the database kept —
        // now resolved by precedence, so it may be a record this request did not write at all.
        setBooking((prev) => ({ ...prev, effectiveCheckout: data.checkout || null }));
        setCheckoutEntryOpen(false);
        // …then reconcile the FULL record list. The response above carries only the winning record,
        // so without this the withdrawn rows beneath it (D70) would still describe the state before
        // this action. Optimistic first for the instant answer, authoritative second.
        fetchBookingDetails();
      } else {
        setCheckoutError('تعذّر حفظ تسجيل الخروج');
      }
    } catch (err) {
      console.error('Error recording guest checkout:', err);
      // Translate the CODE, never the server's message text (I18N_STRATEGY).
      const code = err.response?.data?.code;
      setCheckoutError(
        code === DEPARTURE_ERROR_CODES.CHECKOUT_TIME_INVALID
          ? 'وقت الخروج غير صالح — لا يمكن أن يكون في المستقبل أو قبل وصول الضيف'
          : code === DEPARTURE_ERROR_CODES.CHECKOUT_NOT_APPLICABLE
            ? 'الحجز ملغي — لا يوجد ضيف ليغادر'
            : 'تعذّر حفظ تسجيل الخروج — حاول مرة أخرى'
      );
    } finally {
      setSavingCheckout(false);
    }
  };

  // Undo a mis-press. Both the record and its removal stay on the booking's change log, with the
  // observed hour in the summary — an undo must not erase what it undoes.
  const handleUndoCheckout = async () => {
    try {
      setSavingCheckout(true);
      setCheckoutError(null);
      // The source of the record ON SCREEN. A booking can carry an ops confirmation and the guest's
      // own plaque report; the endpoint withdraws the one it is named, and omitting this silently
      // withdraws nothing when the record shown is the guest's.
      const data = await departureApi.undoGuestCheckout(bookingId, booking?.effectiveCheckout?.source);
      // ⚠️ MIRROR THE SERVER, do not assume the row is now empty. That assumption was correct while
      // one record was possible and became a lie the moment a second source existed: withdrawing the
      // ops record on a booking that also carries a guest report leaves the GUEST's record
      // governing, and clearing the row here would contradict the very next refresh (D47).
      setBooking((prev) => ({ ...prev, effectiveCheckout: data?.checkout || null }));
      // The record just withdrawn must APPEAR as withdrawn (D70), and only a refetch carries the
      // withdrawal's name and hour — the response above names the survivor, not the casualty.
      fetchBookingDetails();
    } catch (err) {
      console.error('Error undoing the checkout record:', err);
      setCheckoutError('تعذّر التراجع عن تسجيل الخروج');
    } finally {
      setSavingCheckout(false);
    }
  };

  /* ── Ops check-in confirmation (tasks/manual-guest-arrival-confirm.md) ───────────────────────
   *
   * 🔴 A PERSON'S WORD, NOT LOCK EVIDENCE. It never changes the lock verdict above it, never clears a
   *    lock warning, and never stops the code repair — the guest's own code is still broken. Its one
   *    consumer is the in-stay message «رسالة الاطمئنان».
   */
  const openArrivalEntry = () => {
    setArrivalValue(toRiyadhInputValue(new Date()));
    setArrivalConfirmError(null);
    setArrivalEntryOpen(true);
  };

  const handleRecordArrival = async () => {
    const observedAt = riyadhInputValueToIso(arrivalValue);
    if (!observedAt) {
      setArrivalConfirmError('وقت الدخول غير صالح');
      return;
    }
    try {
      setSavingArrival(true);
      setArrivalConfirmError(null);
      const data = await guestArrivalApi.recordGuestArrival(bookingId, observedAt);
      if (data?.success) {
        // The server's WINNING record, not the value typed — another operator may have been first.
        setBooking((prev) => ({ ...prev, opsArrival: data.arrival || null, canConfirmArrival: false }));
        setArrivalEntryOpen(false);
        fetchBookingDetails();
      } else {
        setArrivalConfirmError('تعذّر حفظ تأكيد الدخول');
      }
    } catch (err) {
      console.error('Error recording the check-in confirmation:', err);
      // Translate the CODE, never the server's message text (I18N_STRATEGY).
      const code = err.response?.data?.code;
      setArrivalConfirmError(
        code === ARRIVAL_ERROR_CODES.CHECKIN_TIME_INVALID
          ? 'وقت الدخول غير صالح — لا يمكن أن يكون في المستقبل أو خارج فترة الإقامة'
          : code === ARRIVAL_ERROR_CODES.CHECKIN_NOT_APPLICABLE
            ? 'الحجز ملغي — لا يوجد ضيف ليدخل'
            : 'تعذّر حفظ تأكيد الدخول — حاول مرة أخرى'
      );
    } finally {
      setSavingArrival(false);
    }
  };

  const handleUndoArrival = async () => {
    try {
      setSavingArrival(true);
      setArrivalConfirmError(null);
      const data = await guestArrivalApi.undoGuestArrival(bookingId);
      setBooking((prev) => ({ ...prev, opsArrival: data?.arrival || null }));
      // The withdrawal must APPEAR (muted line below) and the button come back — only a refetch
      // carries both.
      fetchBookingDetails();
    } catch (err) {
      console.error('Error undoing the check-in confirmation:', err);
      setArrivalConfirmError('تعذّر التراجع عن تأكيد الدخول');
    } finally {
      setSavingArrival(false);
    }
  };

  const formatCurrency = (amount, currency = 'SAR') => {
    if (amount == null || amount === '') return 'غير محدد';
    // 2dp always: a VAT-inclusive total leaves ragged figures like 2608.7, which reads as
    // a different number to anyone reconciling it against an invoice.
    const n = Number(amount);
    return Number.isFinite(n) ? `${n.toFixed(2)} ${currency}` : 'غير محدد';
  };

  const getPlatformColor = (platform) => {
    const colors = {
      airbnb: '#FF5A5F',
      booking: '#003580',
      gathern: '#00A699',
      direct: '#6c757d',
      default: '#6c757d'
    };
    return colors[platform?.toLowerCase()] || colors.default;
  };

  const getPlatformDisplayName = (platform) => {
    const names = {
      direct: 'Host Me',
      airbnb: 'Airbnb',
      booking: 'Booking.com',
      gathern: 'Gathern',
      channex: 'Channex',
      ical: 'iCal'
    };
    return names[platform?.toLowerCase()] || platform;
  };

  const getStatusColor = (status) => {
    const colors = {
      detected: '#17a2b8',   // Old status
      confirmed: '#28a745',  // Old status
      upcoming: '#17a2b8',   // New status (same as detected)
      active: '#28a745',     // New status (same as confirmed)
      completed: '#6c757d',
      cancelled: '#dc3545'
    };
    return colors[status] || colors.detected;
  };

  if (loading) {
    return (
      <DesktopLayout 
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle="تفاصيل الحجز"
      >
        <div className="booking-details-loading" dir="rtl">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  if (error) {
    return (
      <DesktopLayout 
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle="تفاصيل الحجز"
      >
        <div className="booking-details-error" dir="rtl">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">
            العودة
          </button>
        </div>
      </DesktopLayout>
    );
  }

  if (!booking) {
    return (
      <DesktopLayout 
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle="تفاصيل الحجز"
      >
        <div className="booking-details-error" dir="rtl">
          <div className="alert alert-warning" role="alert">
            الحجز غير موجود
          </div>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">
            العودة
          </button>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout 
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="تفاصيل الحجز"
    >
      <div className="booking-details-container" dir="rtl">
      {/* FIRST thing on the page, above the title and the actions. Every field below this line —
          the unit, the door code, the cleaning, the readiness — still resolves from the BOOKING
          until stage 2 migrates them, so a reader who misses this acts on the wrong apartment.
          It renders nothing at all for a guest who has not been moved, which is all of them. */}
      <RelocationBanner relocation={relocation} className="mb-3" />

      {/* We looked and could not get an answer. Stated, never left as blank space — see
          RELOCATION_UNKNOWN_AR. */}
      {isCoreStaff && relocationError && (
        <div className="alert alert-warning mb-3" role="alert">
          <i className="bi bi-exclamation-triangle"></i> {RELOCATION_UNKNOWN_AR} أعد تحميل الصفحة، وإن تكرّر الأمر أبلغ الفريق التقني.
        </div>
      )}

      {isCoreStaff && (
        <RelocateGuestModal
          show={showRelocate}
          onHide={() => setShowRelocate(false)}
          booking={booking}
          onRelocated={() => { refreshRelocation(); setRelocationVersion(v => v + 1); }}
        />
      )}

      <div className="booking-details-header">
        <h1>تفاصيل الحجز</h1>
        <div className="d-flex gap-2">
          {booking.status !== 'cancelled' && booking.status !== 'completed' && booking.sourcePlatform === 'direct' && (user?.role === 'admin' || user?.role === 'ops-manager') && (
            <button
              onClick={() => setShowEditModal(true)}
              className="btn btn-primary touch-btn"
            >
              <i className="bi bi-pencil-square"></i> تعديل الحجز
            </button>
          )}
          {/* Cancel button for PMS-owned bookings only (direct/manual). Hidden for OTA sources that
              the PMS doesn't own the cancellation for: Channex (Airbnb/Booking.com) AND iCal (Gathern) —
              those must be cancelled on the source platform, so the info banner below is shown instead. */}
          {booking.status !== 'cancelled' && booking.status !== 'completed' && !booking.channexData?.bookingId && !booking.sourceType?.startsWith('ical') && (user?.role === 'admin' || user?.role === 'ops-manager') && (
            <button
              onClick={handleCancelBooking}
              className="btn btn-danger touch-btn"
              disabled={cancelling}
            >
              <i className="bi bi-x-circle"></i> {cancelling ? 'جاري الإلغاء...' : 'إلغاء الحجز'}
            </button>
          )}
          {/* Relocation is ops work on a LIVE stay, so it is offered only while there is a guest to
              move — never on a cancelled or finished booking, and never to an owner. It sits with
              the other booking actions rather than inside the banner: the banner states a fact and
              must not also be a control (someone opening the page for an unrelated reason should
              not find a button that moves a guest).
              `relocationKnown` gates it on having ACTUALLY heard back: on a failed lookup
              `relocation` is null, which would re-offer the move on a guest who has already been
              moved — and while the lookup is still in flight it would flash on and off. */}
          {isCoreStaff && booking.status !== 'cancelled' && booking.status !== 'completed' && relocationKnown && !relocation && (
            <button
              onClick={() => setShowRelocate(true)}
              className="btn btn-warning touch-btn"
            >
              <i className="bi bi-box-arrow-right"></i> {RELOCATION_ACTION}
            </button>
          )}
          <button onClick={() => navigate(-1)} className="btn btn-secondary touch-btn">
            <i className="bi bi-arrow-right"></i> العودة
          </button>
        </div>
      </div>

      {/* Note for OTA-sourced bookings — Channex (Airbnb/Booking.com) + iCal (Gathern).
          Directs ops to manage cancellation/modification on the source platform (hidden for TurnKey owners). */}
      {!isTurnKeyOwner && (booking.channexData?.bookingId || booking.sourceType?.startsWith('ical')) && booking.status !== 'cancelled' && booking.status !== 'completed' && (
        <div className="alert alert-info" dir="rtl" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <i className="bi bi-info-circle"></i>
          <strong> ملاحظة: </strong>
          للتعديل أو الإلغاء، يرجى إدارة هذا الحجز مباشرة على منصة {getPlatformDisplayName(booking.sourcePlatform)}
        </div>
      )}

      <div className="booking-details-content">
        {/* What is LEFT of a move — FIRST card, above the commercial detail: an operator who opens
            this page mid-incident needs "where is the guest" (the banner) and then "what still has
            to happen", in that order and without scrolling. Inside the content grid rather than
            floated above it, so it inherits the 20px gap every other card on this page relies on —
            outside it, the card had nothing beneath it and butted into the next block.
            Renders nothing at all unless this booking has a move on it, which is every booking.

            `onRelocateAgain` opens the SAME modal the header button does — the panel only triggers
            it, so there is one entry point and one set of refusals. It matters because the header's
            own «نقل الضيف» is hidden while a move exists: once the guest has physically moved, this
            is the only route to a move-back (which O1 depends on) or onward to a third unit. */}
        <RelocationStepsPanel
          bookingId={bookingId}
          isCoreStaff={isCoreStaff}
          version={relocationVersion}
          onChanged={refreshRelocation}
          onRelocateAgain={booking.status !== 'cancelled' && booking.status !== 'completed'
            ? () => setShowRelocate(true)
            : undefined}
        />

        {/* Basic Information Card */}
        <div className="details-card">
          <h2>معلومات أساسية</h2>
          <div className="details-grid">
            <div className="detail-item">
              <label>الرقم المرجعي الداخلي:</label>
              <span className="detail-value confirmation-code">
                {booking._id}
              </span>
            </div>
            <div className="detail-item">
              <label>رقم التأكيد من منصة الحجز:</label>
              <span className="detail-value">
                {booking.confirmationCode ||
                 booking.enrichedData?.confirmationCode ||
                 (booking.sourcePlatform === 'direct' ? `HM-${booking._id.slice(-8).toUpperCase()}` : 'غير متوفر')}
              </span>
            </div>
            <div className="detail-item">
              <label>العقار:</label>
              <Link to={`/properties/${booking.property._id}`} className="property-link">
                {booking.property.name}
              </Link>
              {booking.property.propertyCode && (
                <span className="badge bg-light text-dark ms-2 fw-normal" style={{ fontSize: '0.75em' }} dir="ltr">{booking.property.propertyCode}</span>
              )}
            </div>
            <div className="detail-item">
              <label>المنصة:</label>
              <span
                className="platform-badge"
                style={{ backgroundColor: getPlatformColor(booking.sourcePlatform) }}
              >
                {getPlatformDisplayName(booking.sourcePlatform)}
              </span>
            </div>
            {/* ── Commission tier ──────────────────────────────────────────────
                Shown as a plain row only when it is NOT the default — every booking is
                Host-Me-sourced unless a person said otherwise, so asserting that on every
                screen is noise. The control appears for billing roles on manual bookings
                and is deliberately available on a COMPLETED stay: the misclassification
                is normally found while preparing the month, long after checkout. */}
            {(booking.sourcedBy === 'owner' || (canSetTier && isManualBooking && booking.status !== 'cancelled')) && (
              <div className="detail-item">
                <label>مصدر الحجز:</label>
                <span>
                  {booking.sourcedBy === 'owner' ? 'حجز وفّره المالك بنفسه' : 'من هوست مي'}
                  {canSetTier && isManualBooking && booking.status !== 'cancelled' && !tierEditing && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      style={{ marginInlineStart: '0.5rem' }}
                      onClick={() => { setTierEditing(true); setTierError(null); setTierReason(''); }}
                    >
                      تغيير
                    </button>
                  )}
                </span>
              </div>
            )}

            {tierEditing && (
              <div className="detail-item" style={{ display: 'block' }}>
                <input
                  type="text"
                  className="form-control form-control-sm mb-2"
                  placeholder="سبب التغيير (اختياري)"
                  value={tierReason}
                  onChange={(e) => setTierReason(e.target.value)}
                  disabled={tierSaving}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={tierSaving}
                  onClick={() => handleTierChange(booking.sourcedBy === 'owner' ? 'host_me' : 'owner')}
                >
                  {booking.sourcedBy === 'owner' ? 'تحويل إلى «من هوست مي»' : 'تحويل إلى «من المالك»'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ marginInlineStart: '0.5rem' }}
                  disabled={tierSaving}
                  onClick={() => { setTierEditing(false); setTierError(null); }}
                >
                  إلغاء
                </button>
                {/* The 409 case names the month to reopen — «حدث خطأ» would leave ops
                    guessing which month is blocking them. */}
                {tierError && <div className="text-danger mt-2" style={{ fontSize: '0.85rem' }}>{tierError}</div>}
              </div>
            )}
            <div className="detail-item">
              <label>الحالة:</label>
              <span 
                className="status-badge"
                style={{ backgroundColor: getStatusColor(booking.status) }}
              >
                {booking.status === 'detected' ? 'تم الكشف' :
                 booking.status === 'confirmed' ? 'مؤكد' :
                 booking.status === 'upcoming' ? 'حجز مؤكد' :
                 booking.status === 'active' ? 'حجز نشط' :
                 booking.status === 'completed' ? 'مكتمل' :
                 booking.status === 'cancelled' ? 'ملغي' : booking.status}
              </span>
            </div>
          </div>
        </div>

        {/* Guest Information Card */}
        <div className="details-card">
          <h2>معلومات الضيف</h2>
          <div className="details-grid">
            <div className="detail-item">
              <label>اسم الضيف:</label>
              <span className="detail-value">{booking.guestName || booking.enrichedData?.guestName || 'غير محدد'}</span>
            </div>
            {booking.guestEmail && (
              <div className="detail-item">
                <label>البريد الإلكتروني:</label>
                <span className="detail-value" style={{ direction: 'ltr', display: 'inline-block' }}>{booking.guestEmail}</span>
              </div>
            )}
            {booking.guestPhone && (
              <div className="detail-item">
                <label>رقم الهاتف:</label>
                <span className="detail-value" style={{ direction: 'ltr', display: 'inline-block' }}>{booking.guestPhone}</span>
              </div>
            )}
            {booking.numberOfGuests && (
              <div className="detail-item">
                <label>عدد الضيوف:</label>
                <span className="detail-value">{booking.numberOfGuests}</span>
              </div>
            )}
          </div>
        </div>

        {/* Booking Dates Card */}
        <div className="details-card">
          <h2>تواريخ الحجز</h2>
          <div className="details-grid">
            <div className="detail-item">
              <label>تاريخ الوصول:</label>
              <span className="detail-value">{formatDate(booking.checkIn)}</span>
            </div>
            <div className="detail-item">
              <label>تاريخ المغادرة:</label>
              <span className="detail-value">{formatDate(booking.checkOut)}</span>
            </div>
            <div className="detail-item">
              <label>عدد الليالي:</label>
              <span className="detail-value">
                {Math.ceil((new Date(booking.checkOut) - new Date(booking.checkIn)) / (1000 * 60 * 60 * 24))}
              </span>
            </div>
          </div>
        </div>

        {/* Access Code Card (TTLock Phase 2b) — admin/ops/ops-manager only.
            Read-only (L26: validity derives from the booking; no code editing here).
            Prominent so ops can deliver it; state badge derived live (L21). */}
        {isCoreStaff && (booking.accessCode || booking.accessStatus) && (() => {
          const stay = deriveStayState(booking);
          const lock = deriveLockState(booking.accessLifecycle);
          // The must-be-visible combo (L48): an active stay whose code never landed.
          const danger = stay.key === 'active' && booking.accessLifecycle === 'FAILED';
          return (
          <div className="details-card access-code-card">
            <h2>رمز الدخول</h2>
            {danger && (
              <div className="access-warning" role="alert">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <span>الحجز نشط والرمز لم يصل إلى القفل — قد يكون الضيف عند الباب بدون رمز. يلزم تدخّل فوري.</span>
              </div>
            )}
            <div className="access-code-display">
              <span className="access-code-value">{booking.accessCode || '—'}</span>
              <span className="access-badge-group">
                <span className="access-badge-label">الحجز:</span>
                <span className={`access-badge ${stay.cls}`}>{stay.label}</span>
              </span>
              {lock && (
                <span className="access-badge-group">
                  <span className="access-badge-label">القفل:</span>
                  <span className={`access-badge ${lock.cls}`}>{lock.label}</span>
                </span>
              )}
            </div>
            <div className="details-grid">
              <div className="detail-item">
                <label>صالح من:</label>
                <span className="detail-value">
                  {booking.accessValidFrom ? formatDateTime(booking.accessValidFrom) : '—'}
                </span>
              </div>
              <div className="detail-item">
                <label>صالح حتى:</label>
                <span className="detail-value">
                  {booking.accessValidUntil ? formatDateTime(booking.accessValidUntil) : '—'}
                </span>
              </div>
            </div>
            <p className="access-code-hint">
              يُدار هذا الرمز تلقائياً حسب فترة الحجز. لتعديل وقت الوصول أو المغادرة استخدم بطاقة "الإعدادات التشغيلية".
            </p>
          </div>
          );
        })()}

        {/* Operational Settings Card (TTLock Phase 2a) — admin/ops/ops-manager only.
            PMS-owned arrival/departure operational times; editable on ANY booking incl. OTA.
            Reservation dates above stay locked; this is the editable operational layer. */}
        {isCoreStaff && (
          <div className="details-card">
            <h2>الإعدادات التشغيلية</h2>
            {!editingOps ? (
              <>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>وقت الوصول المتوقع:</label>
                    <span className="detail-value">
                      {booking.expectedArrivalAt ? (
                        <>
                          {formatDateTime(booking.expectedArrivalAt)}
                          {booking.expectedArrivalSource === 'ops' && (
                            <span className="ops-time-badge ops-time-badge--manual">حدده الفريق</span>
                          )}
                          {booking.expectedArrivalSource === 'channex' && (
                            <span className="ops-time-badge ops-time-badge--ota">من المنصة</span>
                          )}
                        </>
                      ) : (
                        <span className="ops-time-default">غير محدد — يُستخدم وقت تسجيل الدخول الافتراضي للعقار</span>
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>وقت المغادرة المتوقع:</label>
                    <span className="detail-value">
                      {booking.expectedDepartureAt ? (
                        formatDateTime(booking.expectedDepartureAt)
                      ) : (
                        <span className="ops-time-default">غير محدد — يُستخدم وقت تسجيل الخروج الافتراضي للعقار</span>
                      )}
                    </span>
                  </div>
                </div>
                {opsNotice && <p className="turnover-note turnover-note--tight ops-saved-notice">{opsNotice}</p>}
                {booking.status !== 'cancelled' && booking.status !== 'completed' ? (
                  <button className="btn btn-outline-primary btn-sm ops-edit-btn" onClick={startEditingOps}>
                    تعديل أوقات التشغيل
                  </button>
                ) : (
                  <p className="ops-time-default">
                    لا يمكن التعديل لحجز {booking.status === 'cancelled' ? 'ملغي' : 'مكتمل'}
                  </p>
                )}
              </>
            ) : (
              <div className="ops-edit-form">
                <div className="details-grid">
                  <div className="detail-item">
                    <label>وقت الوصول المتوقع:</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={opsArrival}
                      onChange={(e) => setOpsArrival(e.target.value)}
                    />
                    <TurnoverWindowNote
                      side="arrival"
                      data={opsTurnover?.arrival}
                      blocking={opsTurnover?.blockedSide === 'arrival'}
                      relocated={Boolean(opsTurnover?.relocated)}
                      loading={opsTurnoverLoading}
                    />
                  </div>
                  <div className="detail-item">
                    <label>وقت المغادرة المتوقع:</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={opsDeparture}
                      onChange={(e) => setOpsDeparture(e.target.value)}
                    />
                    <TurnoverWindowNote
                      side="departure"
                      data={opsTurnover?.departure}
                      blocking={opsTurnover?.blockedSide === 'departure'}
                      relocated={Boolean(opsTurnover?.relocated)}
                      loading={opsTurnoverLoading}
                    />
                  </div>
                </div>
                {opsError && <div className="alert alert-danger ops-edit-error">{opsError}</div>}
                <div className="ops-edit-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveOperationalTimes}
                    disabled={savingOps || opsTurnover?.verdict === TURNOVER_VERDICT.BLOCK}
                  >
                    {savingOps ? 'جاري الحفظ...' : 'حفظ'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingOps(false)} disabled={savingOps}>
                    إلغاء
                  </button>
                  <button
                    className="btn btn-link btn-sm"
                    onClick={() => { setOpsArrival(''); setOpsDeparture(''); }}
                    disabled={savingOps}
                  >
                    إعادة للوضع الافتراضي
                  </button>
                </div>
                <p className="ops-edit-hint">
                  الأوقات تُدخل بتوقيت الرياض. اترك الحقل فارغًا لاستخدام الوقت الافتراضي للعقار.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Access & Occupancy Card (حالة تسجيل دخول وخروج الضيف) — admin/ops/ops-manager only.
            Answers the question رمز الدخول raises: the code exists — did it actually open
            the door, and did the guest then leave? Placed after الإعدادات التشغيلية.

            🔴 ONE CARD, TWO ROWS OF DIFFERENT EVIDENTIAL WEIGHT (D19,
               tasks/manual-guest-checkout-event.md). دخول is machine evidence from the smart lock
               and is read-only — the verdict comes from the backend resolver, nothing is derived
               here (tasks/guest-arrival-state.md). Beneath it, a SEPARATE human confirmation for a
               guest the lock could not see (tasks/manual-guest-arrival-confirm.md) — its own words,
               its own source line, and it never changes the lock verdict. خروج is a HUMAN confirming a physical fact
               nobody watched, and it is the one thing on this card an operator can write.
               Each row names its own source out loud so neither can be read as evidence for the
               other. Do not merge the two source lines, and do not give خروج the lock's green.

            Rendered for ALL core staff, not only when arrival data arrived: the checkout row is
            always actionable, and a card that vanished on an arrival lookup failure would take the
            checkout button with it. */}
        {isCoreStaff && (() => {
          const arrivalPending = !arrival && !arrivalError;
          const view = arrivalError
            ? { label: 'تعذّر جلب الحالة', cls: 'arrival-badge-error' }
            : (ARRIVAL_VIEW[arrival?.status] || ARRIVAL_VIEW.NOT_APPLICABLE);
          const arrived = !arrivalError && arrival?.status === 'ARRIVED';
          // WHICH record governs is decided by the backend, never here (checkout-tap-url D46/D47) —
          // a copy of the precedence rule in a component is a second answer waiting to drift from
          // the crew card and the readiness column.
          const checkout = booking.effectiveCheckout || null;
          return (
          <div className="details-card arrival-card" id="booking-checkout">
            <h2>حالة تسجيل دخول وخروج الضيف</h2>

            {/* ─── دخول — machine evidence (smart lock) ─────────────────────── */}
            <div className="evidence-row">
              <span className="evidence-row-label">دخول</span>

              {arrivalPending ? (
                <p className="arrival-hint">جارٍ التحقق من حالة الدخول…</p>
              ) : (
                <>
                  <div className="arrival-display">
                    <span className={`access-badge ${view.cls}`}>{view.label}</span>
                    {/* The timestamp is the answer ops actually wants, so it carries the
                        weight — same visual rank as the door code next door. */}
                    {arrived && (
                      <span className="arrival-time">{formatDateTime(arrival.arrivedAt)}</span>
                    )}
                  </div>

                  {arrived && (
                    <div className="details-grid">
                      <div className="detail-item">
                        <label>عدد مرات الدخول:</label>
                        <span className="detail-value">{formatCount(arrival.entryCount)}</span>
                      </div>
                      <div className="detail-item">
                        <label>آخر دخول:</label>
                        <span className="detail-value">{formatDateTime(arrival.lastEntryAt)}</span>
                      </div>
                    </div>
                  )}

                  {/* A broken lookup says so, out loud. Silence here would be the same
                      failure the whole card exists to prevent.
                      The shared half carries the honesty clause ("do not read this as
                      evidence either way"); the advice that follows is page-specific —
                      "reload" is the right next step here and meaningless in the inbox
                      panel, which is why only the first half is shared. */}
                  {arrivalError && (
                    <p className="arrival-hint">
                      {ARRIVAL_ERROR_AR} أعد تحميل الصفحة، وإن تكرّر الأمر أبلغ الفريق التقني.
                    </p>
                  )}

                  {/* Absence is stated, then immediately qualified. Ops must never read
                      "no record" as "no guest". */}
                  {!arrivalError && arrival.status === 'NO_EVIDENCE' && (
                    <p className="arrival-hint">{ARRIVAL_NO_EVIDENCE_HINT}</p>
                  )}
                  {!arrivalError && arrival.status === 'NOT_APPLICABLE' && (
                    <p className="arrival-hint">
                      {ARRIVAL_REASON_AR[arrival.reason] || ARRIVAL_REASON_UNKNOWN}
                    </p>
                  )}

                  {!arrivalError && (
                    <p className="arrival-source">
                      المصدر: سجلات القفل الذكي — يُحتسب الدخول برمز هذا الحجز فقط (لا يشمل دخول الفريق برموز أخرى).
                    </p>
                  )}
                </>
              )}

              {/* ─── تأكيد الفريق — the HUMAN half of this row (tasks/manual-guest-arrival-confirm.md).
                  For a guest the team let in on another code, whom the lock above could not see.
                  🔴 Its own words and its own source line, never the lock's: «أكد الفريق دخول الضيف»
                  is a person's word (D19, same rule as خروج below). Wording kept in step with the
                  readiness card's `arrivalReadiness.confirmArrival.*` keys — change both together. */}
              {booking.opsArrival ? (
                <div className="ops-arrival">
                  <div className="arrival-display">
                    <span className="access-badge checkout-badge-recorded">أكد الفريق دخول الضيف</span>
                    {/* When the guest was inside — not when the record was made. */}
                    <span className="checkout-time">{formatDateTime(booking.opsArrival.observedAt)}</span>
                  </div>
                  <p className="arrival-source">
                    المصدر: تأكيد يدوي من الفريق — ليس رصداً من القفل.
                    {booking.opsArrival.recordedByName ? ` سجّله ${booking.opsArrival.recordedByName}.` : ''}
                  </p>
                  {booking.status !== 'cancelled' && (
                    <button
                      className="btn btn-link btn-sm p-0"
                      onClick={handleUndoArrival}
                      disabled={savingArrival}
                      style={{ fontSize: '0.82rem' }}
                    >
                      تراجع عن التأكيد
                    </button>
                  )}
                </div>
              ) : (
                // Offered when the server says the stay window is open (`canConfirmArrival`) AND the
                // lock has not seen the guest's own code. Hidden while the lock answer is loading.
                booking.canConfirmArrival && !arrivalPending && !arrived && !arrivalEntryOpen && (
                  <>
                    <p className="arrival-hint">
                      إن دخل الضيف برمز آخر (داخلي أو طوارئ) فلن يرصده القفل — أكّد دخوله هنا.
                    </p>
                    <button
                      className="btn btn-outline-primary btn-sm ops-edit-btn"
                      onClick={openArrivalEntry}
                    >
                      تأكيد الدخول
                    </button>
                  </>
                )
              )}

              {arrivalEntryOpen && (
                <div className="checkout-entry">
                  <label htmlFor="arrival-observed-at">وقت دخول الضيف:</label>
                  <input
                    id="arrival-observed-at"
                    type="datetime-local"
                    value={arrivalValue}
                    max={toRiyadhInputValue(new Date())}
                    onChange={(e) => setArrivalValue(e.target.value)}
                  />
                  <p className="checkout-entry-hint">
                    بتوقيت الرياض. عدّل الساعة إن كان الضيف قد دخل قبل الآن.
                  </p>
                  {arrivalConfirmError && <div className="checkout-entry-error">{arrivalConfirmError}</div>}
                  <div className="checkout-entry-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleRecordArrival}
                      disabled={savingArrival || !arrivalValue}
                    >
                      {savingArrival ? 'جاري الحفظ...' : 'حفظ'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setArrivalEntryOpen(false)}
                      disabled={savingArrival}
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              )}

              {/* Withdrawn confirmations stay visible, muted — a withdrawal must not look like
                  nothing happened (checkout D49/D70). */}
              {(booking.arrivalObservations || [])
                .filter((o) => o.status === 'withdrawn')
                .map((o) => (
                  <p key={o.id} className="checkout-withdrawn-line">
                    {`سحب ${o.withdrawnByName || 'الفريق'} تأكيد الدخول — ${formatDateTime(o.withdrawnAt || o.recordedAt)}`}
                  </p>
                ))}

              {arrivalConfirmError && !arrivalEntryOpen && (
                <div className="checkout-entry-error">{arrivalConfirmError}</div>
              )}
            </div>

            {/* ─── خروج — a human assertion, not an observation ───────────────
                ⚠️ Wording locked (D16): never «الخروج الفعلي».
                🔴 AND NEVER THE SAME WORDS FOR BOTH SOURCES (D19). «أكد الفريق» means an operator
                asked and was told; a guest pressing a plaque on their way out is their own word and
                nobody verified it. This page said «أكد الفريق» for both until a dev walkthrough
                caught it on a real guest record.
                Kept in step with the readiness card's `departureReadiness.checkout.*` keys — that
                card is bilingual and goes through the locale files; this page is Arabic-only, so
                the phrases are deliberately duplicated rather than shared. Change both together. */}
            <div className="evidence-row">
              <span className="evidence-row-label">خروج</span>

              {checkout ? (
                <>
                  <div className="arrival-display">
                    <span className="access-badge checkout-badge-recorded">
                      {checkout.source === 'guest' ? 'أبلغ الضيف بمغادرته' : 'أكد الفريق خروج الضيف'}
                    </span>
                    {/* The hour the GUEST left — not when the record was made. The two are stored
                        separately precisely because ops confirm at 14:00 a departure at 09:15. */}
                    <span className="checkout-time">{formatDateTime(checkout.observedAt)}</span>
                  </div>

                  <p className="arrival-source">
                    {checkout.source === 'guest'
                      ? 'المصدر: ضغط الضيف على لوحة الباب — إبلاغ منه، ولم يتحقق منه الفريق.'
                      : 'المصدر: تأكيد يدوي من الفريق — ليس رصداً من جهاز.'}
                    {checkout.recordedByName ? ` سجّله ${checkout.recordedByName}.` : ''}
                  </p>

                  {booking.status !== 'cancelled' && (
                    <button
                      className="btn btn-link btn-sm p-0"
                      onClick={handleUndoCheckout}
                      disabled={savingCheckout}
                      style={{ fontSize: '0.82rem' }}
                    >
                      تراجع عن التسجيل
                    </button>
                  )}

                  {/* 🔴 OPS CAN CONFIRM ON TOP OF A GUEST'S REPORT (D43/D47) — the two are meant to
                      coexist, with the team's word taking precedence. Without this the only action
                      against a guest report was «تراجع», so adding a confirmation meant destroying
                      the guest's record first. Not offered against an ops record: confirming your
                      own confirmation is not a thing. */}
                  {booking.status !== 'cancelled' && checkout.source === 'guest' && !checkoutEntryOpen && (
                    <button
                      className="btn btn-outline-primary btn-sm ops-edit-btn"
                      onClick={openCheckoutEntry}
                      style={{ marginInlineStart: '8px' }}
                    >
                      تأكيد الفريق
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div className="arrival-display">
                    <span className="access-badge checkout-badge-none">لم يُسجَّل خروج الضيف بعد</span>
                  </div>
                  {/* Not a failure and not a task: an empty row is a fact nobody has established
                      yet, not an overdue action, so it must never read as one.

                      ⚠️ CORRECTED 2026-08-04. This said "sends no notification and does not summon
                      the cleaning team". That was true of Part 1 and is now FALSE — the record
                      notifies the crew holding the cleaning task
                      (tasks/manual-guest-checkout-event.md Part 2). Left as it was, an operator
                      would read it, believe nobody had been told, and phone the crew anyway — the
                      exact call this feature exists to remove. The half that still matters is kept:
                      the system informs, it never sends anyone. */}
                  <p className="arrival-hint">
                    يسجَّل خروج الضيف يدوياً بعد تأكيده. يُبلَّغ فريق النظافة بهذا التسجيل، ولا يُستدعى أحد تلقائياً.
                  </p>

                  {booking.status !== 'cancelled' && !checkoutEntryOpen && (
                    <button
                      className="btn btn-outline-primary btn-sm ops-edit-btn"
                      onClick={openCheckoutEntry}
                    >
                      تأكيد الخروج
                    </button>
                  )}

                </>
              )}

              {/* THE ENTRY FORM SITS OUTSIDE THE TERNARY, so both branches can open it: ops record a
                first checkout, AND ops confirm on top of a guest's report (D43/D47). It used to
                live inside the not-recorded branch only, which is why the new «تأكيد الفريق»
                button opened nothing at all. */}
              {checkoutEntryOpen && (
                  <div className="checkout-entry">
                    <label htmlFor="checkout-observed-at">وقت مغادرة الضيف:</label>
                    <input
                      id="checkout-observed-at"
                      type="datetime-local"
                      value={checkoutValue}
                      // A departure cannot be in the future — enforced here for immediate
                      // feedback AND server-side, because `max` is a hint, not a guarantee.
                      max={toRiyadhInputValue(new Date())}
                      onChange={(e) => setCheckoutValue(e.target.value)}
                    />
                    <p className="checkout-entry-hint">
                      بتوقيت الرياض. عدّل الساعة إن كان الضيف قد غادر قبل الآن.
                    </p>
                    {checkoutError && <div className="checkout-entry-error">{checkoutError}</div>}
                    <div className="checkout-entry-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleRecordCheckout}
                        disabled={savingCheckout || !checkoutValue}
                      >
                        {savingCheckout ? 'جاري الحفظ...' : 'حفظ'}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setCheckoutEntryOpen(false)}
                        disabled={savingCheckout}
                      >
                        إلغاء
                      </button>
                    </div>
                  </div>
                )}

              {/* ─── سُحبت — withdrawn records, shown rather than vanished ────────────────────
                  D49/D70/D71 (tasks/checkout-plaque-panel.md).

                  🔴 A WITHDRAWAL MUST NOT LOOK LIKE NOTHING HAPPENED. `effectiveCheckout` above
                  carries only ACTIVE records, so before this block an operator withdrew a wrong
                  guest report and the row simply emptied — no trace that a record had existed, and
                  none that they were the one who removed it. Withdrawn, never deleted (D49).

                  🔴 AND NEVER THE SAME WORDS FOR THE TWO WITHDRAWALS. A guest pressing undo on
                  their own phone and an operator judging a report wrong are different events with
                  different authors; collapsing them into one sentence is the same defect D19
                  names for the two sources. `withdrawalReason` is what separates them — not the
                  presence of a name, which merely happens to be absent for the guest. */}
              {(booking.checkoutObservations || [])
                .filter((o) => o.status === 'withdrawn')
                .map((o) => (
                  <p key={o.id} className="checkout-withdrawn-line">
                    {o.withdrawalReason === 'guest_self_undo'
                      ? `تراجع الضيف عن إبلاغه — ${formatDateTime(o.withdrawnAt || o.recordedAt)}`
                      : `سحب ${o.withdrawnByName || 'الفريق'} ${o.source === 'guest' ? 'إبلاغ الضيف' : 'تأكيد الفريق'} — ${formatDateTime(o.withdrawnAt || o.recordedAt)}`}
                  </p>
                ))}

              {/* An undo failure has no entry form to render inside, so it surfaces here. */}
              {checkoutError && !checkoutEntryOpen && (
                <div className="checkout-entry-error">{checkoutError}</div>
              )}
            </div>
          </div>
          );
        })()}

        {/* OTA Pre-Arrival Notes — surfaces Booking.specialRequests (Q6).
            Anchor target for the banner inside the Guest Requests section. */}
        {booking.specialRequests && booking.specialRequests.trim() && (
          <div className="details-card ota-notes-card" id="booking-ota-notes">
            <h2>
              <i className="bi bi-chat-left-quote ota-notes-icon"></i>
              ملاحظة قبل الوصول من المنصة
            </h2>
            <p className="ota-notes-text">{booking.specialRequests}</p>
          </div>
        )}

        {/* Financial Information Card */}
        <div className="details-card">
          <h2>المعلومات المالية</h2>
          <div className="details-grid">
            {booking.rate ? (
              <>
                <div className="detail-item">
                  <label>قبل الضريبة:</label>
                  <span className="detail-value">{formatCurrency(booking.rate.subtotal, booking.currency)}</span>
                </div>
                <div className="detail-item">
                  <label>الضريبة{vatPercentSuffix(booking.rate.subtotal, booking.rate.tax)}:</label>
                  <span className="detail-value">{formatCurrency(booking.rate.tax, booking.currency)}</span>
                </div>
                <div className="detail-item">
                  <label>المبلغ الإجمالي:</label>
                  <span className="detail-value amount">
                    {formatCurrency(booking.rate.total, booking.currency)}
                  </span>
                </div>
              </>
            ) : (
              <div className="detail-item">
                <label>المبلغ الإجمالي:</label>
                <span className="detail-value amount">
                  {formatCurrency(booking.totalAmount, booking.currency)}
                  {/* Whether VAT sits inside that number is a per-BOOKING fact, not a
                      per-platform one: an owner's VAT number flipped two Airbnb bookings on
                      the same unit two days apart, and Booking.com moved a property the other
                      way. So this READS the stamp the booking already carries and derives
                      nothing — it cannot disagree with the billing worksheet. 'unknown' or a
                      row predating the field renders nothing; a guess here is worse than a
                      gap. The manual-booking branch above needs none of this: it already
                      itemises قبل الضريبة / الضريبة. */}
                  {vatFootingLabel(booking.vatFooting) && (
                    <span className="vat-footing-note">
                      {vatFootingLabel(booking.vatFooting)}
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* OTA fee breakdown — owned here for EVERY source (Channex, Gathern iCal,
                manual), so booking money has one home regardless of who supplied it.
                Each line is gated independently: monthly Gathern entry can legitimately
                land a total before its commission is known, so a partially-filled
                booking must render cleanly rather than show an empty row. */}
            {booking.commission > 0 && (
              <div className="detail-item">
                <label>العمولة:</label>
                <span className="detail-value amount" style={{ color: '#dc3545' }}>
                  -{formatCurrency(booking.commission, booking.currency)}
                </span>
              </div>
            )}
            {booking.netAmount > 0 && booking.netAmount !== booking.totalAmount && (
              <div className="detail-item">
                <label>المبلغ الصافي:</label>
                <span className="detail-value amount" style={{ color: '#28a745', fontWeight: 'bold' }}>
                  {formatCurrency(booking.netAmount, booking.currency)}
                </span>
              </div>
            )}

            {/* Payment Method - Only for manual bookings */}
            {booking.actualPaymentMethod ? (
                <div className="detail-item">
                  <label>طريقة الدفع:</label>
                  <span className="detail-value">
                    {booking.actualPaymentMethod === 'cash' && 'نقدي'}
                    {booking.actualPaymentMethod === 'bank-transfer' && 'تحويل بنكي'}
                    {booking.actualPaymentMethod === 'credit-card' && 'بطاقة ائتمان'}
                    {booking.actualPaymentMethod === 'mada' && 'مدى'}
                    {booking.actualPaymentMethod === 'cheque' && 'شيك'}
                    {booking.actualPaymentMethod === 'other' && 'أخرى'}
                  </span>
                </div>
              ) : null}

            {booking.enrichedData?.totalAmount && (
              <div className="detail-item">
                <label>المبلغ المُحدث:</label>
                <span className="detail-value amount">
                  {formatCurrency(booking.enrichedData.totalAmount, booking.currency)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Guest Requests Hub V1 — Phase 3 (moved up: above admin/debug surfaces for mobile-first ops UX) */}
        <BookingGuestRequestsSection booking={booking} />

        {/* Guest complaints → fixes — the manual way in (tasks/complaints-to-fixes.md C1). Core staff only: this
            page is open to owners, and owners see nothing of a complaint (C2). */}
        {isCoreStaff && (
          <div className="details-card">
            <h2>الشكاوى</h2>
            <p className="text-muted mb-2">سجّل مشكلة واجهها الضيف لنعالجها ونمنع تكرارها.</p>
            <OpenComplaintButton source={{ kind: 'booking', id: booking._id }} />
          </div>
        )}

        {/* Automated check-in message — read-only history (core staff only; hidden from owners) */}
        {isCoreStaff && <BookingCheckinMessagesSection booking={booking} />}

        {/* Technical Information Card - hide from owners */}
        {(user?.role === 'admin' || user?.role === 'ops' || user?.role === 'ops-manager') && (
          <div className="details-card">
            <h2>معلومات تقنية</h2>
            <div className="details-grid">
              <div className="detail-item">
                <label>المرجع الداخلي:</label>
                <span className="detail-value">...{booking._id.slice(-4)}</span>
              </div>
              {booking.iCalUID && (
                <div className="detail-item">
                  <label>معرف iCal:</label>
                  <span className="detail-value small">{booking.iCalUID}</span>
                </div>
              )}
              
              {/* Sync Status - NICE TO HAVE */}
              <div className="detail-item">
                <label>حالة المزامنة:</label>
                <span className="detail-value">
                  <span className="sync-status-badge success">✅ محدث</span>
                </span>
              </div>
              
              {/* Last Seen in Source - NICE TO HAVE */}
              {booking.lastSeenInSource && (
                <div className="detail-item">
                  <label>آخر رؤية في المصدر:</label>
                  <span className="detail-value">{formatDate(booking.lastSeenInSource)}</span>
                </div>
              )}
              
              {/* Sync Version - NICE TO HAVE */}
              {booking.syncMetadata?.version && (
                <div className="detail-item">
                  <label>إصدار المزامنة:</label>
                  <span className="detail-value">{booking.syncMetadata.version}</span>
                </div>
              )}
              
              <div className="detail-item">
                <label>تم تحليل البريد:</label>
                <span className="detail-value">
                  {booking.emailParsed ? (
                    <span className="text-success">✓ نعم</span>
                  ) : (
                    <span className="text-muted">✗ لا</span>
                  )}
                </span>
              </div>
              {booking.parsingConfidence !== undefined && (
                <div className="detail-item">
                  <label>ثقة التحليل:</label>
                  <span className="detail-value">{booking.parsingConfidence}%</span>
                </div>
              )}
              <div className="detail-item">
                <label>تاريخ الإنشاء:</label>
                <span className="detail-value">{formatDate(booking.createdAt)}</span>
              </div>
              <div className="detail-item">
                <label>آخر تحديث:</label>
                <span className="detail-value">{formatDate(booking.updatedAt)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Export Exclusions Card - Only for admin and DIY owners */}
        {user?.role === 'admin' && (
        <div className="details-card">
          <h2>إعدادات التصدير</h2>
          <div className="export-exclusions">
            <label>استثناء من التصدير إلى:</label>
            <div className="exclusion-checkboxes">
              {['airbnb', 'booking', 'expedia', 'gathern'].map(platform => (
                <div key={platform} className="exclusion-item">
                  <input 
                    type="checkbox" 
                    id={`exclude-${platform}`}
                    checked={booking.excludeFromExport?.includes(platform) || false}
                    onChange={(e) => {
                      // Handle exclusion toggle
                      const isChecked = e.target.checked;
                      const currentExclusions = booking.excludeFromExport || [];
                      let newExclusions;
                      
                      if (isChecked) {
                        newExclusions = [...currentExclusions, platform];
                      } else {
                        newExclusions = currentExclusions.filter(p => p !== platform);
                      }

                      // Update booking (you'd need to implement the API call)
                      // Exclusions updated (removed debug log)
                    }}
                  />
                  <label htmlFor={`exclude-${platform}`} className="platform-label">
                    <span 
                      className="platform-color-dot" 
                      style={{ backgroundColor: getPlatformColor(platform) }}
                    ></span>
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </label>
                </div>
              ))}
            </div>
            <div className="exclusion-note">
              <p>الحجوزات المستثناة لن يتم تصديرها للمنصات المحددة لتجنب التعارض</p>
            </div>
          </div>
        </div>
        )}

        {/* Blocking Status Card */}
        {booking.blockingStatus && booking.blockingStatus.length > 0 && (
          <div className="details-card">
            <h2>حالة الحظر على المنصات الأخرى</h2>
            <div className="blocking-list">
              {booking.blockingStatus.map((block, index) => (
                <div key={index} className="blocking-item">
                  <span className="platform-name">{block.platform}:</span>
                  <span className={`blocking-status ${block.blocked ? 'blocked' : 'not-blocked'}`}>
                    {block.blocked ? '✓ محظور' : '✗ غير محظور'}
                  </span>
                  {block.blockedAt && (
                    <span className="blocked-date">
                      ({formatDate(block.blockedAt)})
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Update History Card (hidden for TurnKey owners) */}
        {!isTurnKeyOwner && booking.updateHistory && booking.updateHistory.length > 0 && (
          <div className="details-card">
            <h2>سجل التغييرات في الحجز</h2>
            <div className="update-history-list">
              {booking.updateHistory
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((entry, index) => (
                <div key={`${entry.timestamp}-${index}`} className="history-item">
                  <div className="history-header">
                    <span className="history-date">
                      {formatDateTime(entry.timestamp)}
                    </span>
                    <span className={`history-source ${entry.source}`}>
                      {entry.source === 'manual' ? 'يدوي' :
                       entry.source === 'channex' ? 'Channex' :
                       entry.source === 'ical' ? 'iCal' :
                       entry.source === 'api' ? 'API' :
                       entry.source === 'system' ? 'النظام' : entry.source}
                    </span>
                  </div>
                  <div className="history-summary">
                    {entry.summary}
                  </div>
                  <div className="history-user">
                    بواسطة: {entry.user || 'النظام'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Channex Integration Information */}
        <BookingChannexInfo booking={booking} hideInternalDetails={isTurnKeyOwner} />
      </div>
    </div>

    {/* Edit Booking Modal */}
    {showEditModal && (
      <EditBookingModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        booking={booking}
        onBookingUpdated={() => {
          setShowEditModal(false);
          fetchBookingDetails(); // Refresh booking details after update
        }}
      />
    )}
    </DesktopLayout>
  );
};

export default BookingDetails;
